/**
 * MissingDraftsBanner — banner cross-œuvres "drafts manquants" sur la
 * page `/drafts`.
 *
 * Source unique (Feature D, S5.5 phase 3) : cumul cross-œuvres des kinds
 * éligibles non encore générés, via `useMissingDrafts`.
 *
 * Le bouton "Tout générer" enqueue un batch — le BE filtre déjà les kinds
 * éligibles + skip les déjà-générés (idempotent).
 *
 * Historique : ce banner portait aussi une branche "migration legacy v12"
 * (`useSystemBanners` → `legacy_migration`) qui primait sur Feature D.
 * Retirée en PR-3 — la migration 006 est appliquée, tous les
 * `purged_work_ids` ont été régénérés et le banner était éteint
 * définitivement.
 */

import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import { useEnqueueGenerationBatch, useMissingDrafts } from '@/hooks/useGenerationJobs'

export default function MissingDraftsBanner() {
  const missingQuery = useMissingDrafts()
  const enqueueMutation = useEnqueueGenerationBatch()

  const totalMissing = missingQuery.data?.total_missing_drafts ?? 0
  const totalWorks = missingQuery.data?.total_works ?? 0

  if (missingQuery.isLoading) return null
  if (totalMissing === 0) return null

  const handleEnqueue = () => {
    enqueueMutation.mutate(
      { source: 'drafts_page_batch' },
      {
        onSuccess: (response) => {
          if (response.scheduled_count === 0) {
            toast.info('Aucun draft à générer (tout est déjà couvert ou jobs en cours).')
            return
          }
          toast.success(
            `${response.scheduled_count} job(s) de génération en file d'attente. Les drafts apparaîtront ici à mesure.`,
          )
        },
        onError: (err: Error) => {
          toast.error(err.message ?? 'Échec de la mise en file')
        },
      },
    )
  }

  return (
    <div className="missing-drafts-banner" role="region" aria-label="Drafts manquants">
      <div className="missing-drafts-banner-content">
        <p className="missing-drafts-banner-text">
          <strong>
            {totalMissing} draft{totalMissing > 1 ? 's' : ''} manquant
            {totalMissing > 1 ? 's' : ''}
          </strong>{' '}
          sur {totalWorks} œuvre{totalWorks > 1 ? 's' : ''} (selon les règles d'éligibilité
          actuelles).
        </p>
        <Button
          variant="primary"
          size="md"
          onClick={handleEnqueue}
          loading={enqueueMutation.isPending}
        >
          <Sparkles size={14} aria-hidden="true" />
          Tout générer
        </Button>
      </div>
    </div>
  )
}
